import { Button, Progress } from "nq-component";
import { Select } from "antd";
import BaseListPage from "../../../../base/BaseListPage";
import Table from "../../../../components/Table";
import CardView from "../../../../components/common/CardView";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
} from "../../../../usecases/object";
import PayrollReportsPresenter from "./PayrollReportsPresenter";
import {
  DERIVED_FIELDS,
  getPayrollReportByKey,
  PAYROLL_REPORT_CATEGORIES,
} from "./payrollReportConfig";
import PayrollReportsFilters from "./PayrollReportsFilters";

export default class PayrollReports extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new PayrollReportsPresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
    );
    this.state = {
      objects: [],
      selected: [],
      loading: true,
      total: 0,
      count: 0,
      limit: 10,
      current: 1,
      isExporting: false,
      selectedReportType: null,
      employee: null,
      filterEpoch: 0,
    };
  }

  getCollectionName() {
    return "employees";
  }

  setCurrent(current) {
    this.setState({ current });
  }

  onClickPagination(page) {
    this.presenter.setCurrentPage(page);
  }

  async refetchData() {
    await this.presenter.getObjects();
  }

  handleReportTypeChange(reportKey) {
    this.presenter.setReportType(
      reportKey ? getPayrollReportByKey(reportKey) : null,
    );
  }

  handleEmployeeChange(value) {
    this.presenter.setEmployeeFilter(value);
  }

  getReportTypeOptions() {
    return PAYROLL_REPORT_CATEGORIES.map((category) => ({
      label: (
        <span style={{ color: category.color, fontWeight: 600 }}>
          {category.label}
        </span>
      ),
      title: category.label,
      options: category.reports.map((report) => ({
        label: report.label,
        value: report.key,
        title: report.label,
      })),
    }));
  }

  getTableSchema() {
    const schema = this.getSchema(this.getCollectionName());
    if (!schema) return null;

    const fields = {};
    this.presenter.getReportFields().forEach((field) => {
      const derived = DERIVED_FIELDS[field];
      if (derived) {
        fields[field] = {
          type: derived.type,
          label:
            this.presenter.report?.fieldLabels?.[field] ?? derived.label,
        };
        return;
      }
      if (schema.fields[field]) {
        fields[field] = schema.fields[field];
      }
    });

    return { ...schema, fields };
  }

  render() {
    const {
      objects,
      loading,
      count,
      limit,
      current,
      isExporting,
      selectedReportType,
      employee,
      filterEpoch,
    } = this.state;
    const schema = this.getTableSchema();

    if (!schema) return <Progress />;

    const reportFields = this.presenter.getReportFields();
    const excludeFields = Object.keys(schema.fields).filter(
      (field) => !reportFields.includes(field),
    );

    return (
      <div className="mt-3">
        <CardView style={{ borderRadius: 0 }}>
          <label
            className="form-label text-muted mb-1"
            style={{ fontSize: ".8rem" }}
          >
            Select payroll report
          </label>
          <Select
            className="w-100"
            style={{ height: "38px" }}
            showSearch
            allowClear
            placeholder="Select payroll report"
            optionFilterProp="label"
            value={selectedReportType?.key || undefined}
            onChange={this.handleReportTypeChange.bind(this)}
            options={this.getReportTypeOptions()}
            optionRender={(option) => {
              const report = getPayrollReportByKey(option.value);
              return (
                <span className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: report?.color || "#94a3b8",
                      flexShrink: 0,
                    }}
                  />
                  {option.label}
                </span>
              );
            }}
          />

          <PayrollReportsFilters
            employee={employee}
            filterEpoch={filterEpoch}
            onEmployeeChange={this.handleEmployeeChange.bind(this)}
          />
        </CardView>

        <CardView style={{ borderRadius: 0 }}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="text-primary mb-1">Payroll Reports</h5>
              <p className="text-muted mb-0" style={{ fontSize: ".85rem" }}>
                Payroll-specific filters, actions, and report logic can be added
                here independently.
              </p>
            </div>
            <Button
              className="btn bg-white border border-primary text-primary"
              onClick={() => this.presenter.exportToCSV()}
              disabled={isExporting || loading || objects.length === 0}
            >
              {isExporting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Exporting...
                </>
              ) : (
                "Export XLSX"
              )}
            </Button>
          </div>
        </CardView>

        <Table
          progress={loading || isExporting}
          refetch={this.refetchData.bind(this)}
          fields={schema.fields}
          objects={objects}
          collection={schema}
          count={count}
          limit={limit}
          current={current}
          loadPagination={this.onClickPagination.bind(this)}
          excludeFields={excludeFields}
        />
      </div>
    );
  }
}
